/**
 * TvIdle — the Spot-TV signage screen. A passive, "lean-back" display: glanceable
 * clock + drifting ring motif, a live room-status bar, and the "join from your
 * phone" card (rotating share key rendered as a real, scannable QR).
 *
 * The TV has NO interactive controls. Like Zoom Rooms and Google Meet Rooms, the
 * in-room display is a wall-mounted appliance with no pointer, no touch, and nobody
 * standing at it — so it shows status only. Every action (start/join a meeting,
 * in-call controls, reset) happens on the paired Remote. The screen's one job is to
 * tell a person how to take control: scan the QR or type the share key on a phone.
 *
 * Pure presentation: every value is a prop.
 */

import type { CalendarEvent } from '@spaces/contracts';

import { Clock, RingMotif, useNow } from '../ui/Clock.js';
import { Qr } from '../ui/Qr.js';
import { Dot, Logo, Pill } from '../ui/components.js';
import { TvAgenda } from './TvAgenda.js';

export interface TvIdleProps {
    roomName: string;
    roomId: string;
    /** The pickup host shown to the user (config, e.g. "cast.8x8.vc"). */
    shareHost: string;
    /** Branded display form, e.g. "cast.8x8.vc/PSGYVW". */
    shareLabel: string | null;
    /** The short rotating code (rendered highlighted after the host). */
    shareCode: string | null;
    /** The working deep link the QR encodes (opens the Remote pre-filled). */
    shareUrl: string | null;
    remoteCount: number;
    /** The room's upcoming meetings (from roomkeeper). Empty/omitted ⇒ no agenda shown. */
    agenda?: CalendarEvent[];
}

export function TvIdle(props: TvIdleProps): JSX.Element {
    const now = useNow();
    const agenda = props.agenda ?? [];

    return (
        <div className = "tv-idle" data-testid = "tv-idle">
            <RingMotif className = "tv-idle__rings" />

            <div className = "tvbar">
                <Logo />
                <span className = "tvbar__sub">|</span>
                <span className = "tvbar__title" data-testid = "tv-room-name">{ props.roomName }</span>
                <Pill label = "Available" variant = "avail" dot = { true } />
                <span className = "spacer" />
                <span className = "tvbar__remotes" data-testid = "tv-roster">
                    <Dot kind = { props.remoteCount > 0 ? 'blue' : 'idle' } />
                    { props.remoteCount } { props.remoteCount === 1 ? 'remote' : 'remotes' } connected
                </span>
            </div>

            <div className = "tv-idle__clock">
                <Clock now = { now } />
            </div>

            <div className = "tv-idle__panel">
                { props.agenda !== undefined ? <TvAgenda events = { agenda } now = { now.getTime() } /> : null }

                <div className = "joincard">
                    <div className = "joincard__qrrow">
                        <div className = "joincard__qr">
                            { props.shareUrl ? <Qr value = { props.shareUrl } /> : null }
                        </div>
                        <div style = {{ minWidth: 0 }}>
                            <div className = "joincard__label">JOIN FROM YOUR PHONE</div>
                            <div className = "joincard__url" data-testid = "share-key">
                                { props.shareHost }/<span className = "joincard__code">{ props.shareCode ?? '••••••' }</span>
                            </div>
                        </div>
                    </div>
                    <div className = "joincard__hint">Scan to pick up the remote, or type the code on { props.shareHost } — everything is controlled from your phone</div>
                </div>
            </div>
        </div>
    );
}
